// Location landing pages — unique local content per town for local SEO.

export const AREAS = [
  {
    slug: "lincoln",
    name: "Lincoln",
    county: "Lincolnshire",
    metaDescription:
      "Independent property surveyor in Lincoln — EPCs, condition surveys, snagging, landlord inspections & defect reports across uphill Lincoln, the Cathedral Quarter, West End and surrounding villages.",
    intro:
      "From Victorian terraces in the West End to listed stone properties in the Cathedral Quarter and modern developments along the fringes of the city, Lincoln's housing stock is one of the most varied in the county — and it rewards a surveyor who knows it well.",
    paragraphs: [
      "Uphill Lincoln is dominated by period property: Georgian townhouses, Victorian villas and stone cottages within the conservation area, many with solid walls, original roof structures and the damp and movement quirks that come with age. A standard mortgage valuation tells you almost nothing about these buildings — an independent condition survey or targeted defect report does.",
      "Downhill and towards North Hykeham, Birchwood and Cherry Willingham, the stock shifts to inter-war semis, ex-local-authority housing and large modern estates, where new build snagging surveys and EPCs are the most requested services. Lincoln's strong rental market — driven by the university and the hospital — also makes independent landlord inspection reports a sensible protection for local portfolios.",
    ],
    popularServices: [
      "property-condition-surveys",
      "defect-reporting",
      "domestic-epcs",
      "landlord-inspection-reports",
    ],
    nearby: ["sleaford", "grantham", "louth"],
  },
  {
    slug: "boston",
    name: "Boston",
    county: "Lincolnshire",
    metaDescription:
      "Independent property surveyor in Boston, Lincolnshire — damp and defect reports, EPCs, condition surveys and landlord inspections across Boston, Wyberton, Kirton and the surrounding Fens.",
    intro:
      "Boston sits at the heart of the Lincolnshire Fens, where a high water table, clay soils and older solid-wall housing combine to make damp, drainage and movement the most common concerns raised by buyers and owners.",
    paragraphs: [
      "Much of central Boston's housing is Victorian and Edwardian terracing — solid brick walls, suspended timber floors and slate roofs. These homes perform well when maintained, but rising damp claims, blocked sub-floor ventilation and poorly executed repointing are recurring issues. An independent defect report, backed by moisture and thermal diagnostics, separates genuine problems from sales-pitch remedies.",
      "Around Wyberton, Kirton, Swineshead and Fishtoft, newer developments bring different questions: snagging on new builds, EPC ratings for lettings, and condition surveys for buyers moving into the area. With Boston's sizeable private rental sector, dated, photographed landlord inspection reports are one of the most cost-effective protections a local landlord can commission.",
    ],
    popularServices: [
      "defect-reporting",
      "domestic-epcs",
      "landlord-inspection-reports",
      "new-build-snagging-surveys",
    ],
    nearby: ["spalding", "sleaford", "skegness"],
  },
  {
    slug: "skegness",
    name: "Skegness",
    county: "Lincolnshire",
    metaDescription:
      "Independent property surveyor in Skegness — condition surveys, roof surveys, EPCs and landlord inspections for coastal homes, holiday lets and chalets in Skegness, Ingoldmells and Chapel St Leonards.",
    intro:
      "Coastal property lives a harder life. Salt-laden winds, driving rain off the North Sea and sandy subsoils mean homes in Skegness, Ingoldmells and Chapel St Leonards age differently to inland stock — and surveys here need to reflect that.",
    paragraphs: [
      "Roof coverings, flashings, render and external joinery all weather faster on the coast. Roof surveys and condition surveys in Skegness routinely pick up slipped tiles, corroded fixings, failed flat-roof coverings and window frames at the end of their life — issues that are inexpensive to catch early and costly to ignore.",
      "The area's large holiday-let and seasonal rental market adds another layer: EPCs for letting compliance, inventory-grade inspection reports between occupancies, and defect reports on chalets and park-adjacent properties where construction quality varies widely. Whether you're buying a permanent home or protecting a coastal investment, an independent local opinion pays for itself.",
    ],
    popularServices: [
      "roof-surveys",
      "property-condition-surveys",
      "domestic-epcs",
      "landlord-inspection-reports",
    ],
    nearby: ["louth", "boston", "lincoln"],
  },
  {
    slug: "spalding",
    name: "Spalding",
    county: "Lincolnshire",
    metaDescription:
      "Independent property surveyor in Spalding — EPCs, new build snagging, condition surveys and defect reports across Spalding, Holbeach, Sutton Bridge and the South Holland Fens.",
    intro:
      "Spalding and the wider South Holland district mix handsome Georgian townhouses along the River Welland with post-war bungalows and some of the fastest-growing new build estates in the county.",
    paragraphs: [
      "The Fenland ground conditions around Spalding — deep silts and a high water table — make settlement cracking, drainage performance and sub-floor damp the questions buyers ask most. A condition survey or focused defect report gives a clear, independent answer before you commit, and is equally useful for owners noticing new cracks or persistent damp.",
      "New development around Spalding, Holbeach, Pinchbeck and Sutton Bridge has been substantial, and pre-completion snagging surveys are increasingly the norm for buyers who want their developer held to the build quality promised. For the area's landlords and agents, EPCs and photographed inspection reports keep lettings compliant and deposits defensible.",
    ],
    popularServices: [
      "new-build-snagging-surveys",
      "property-condition-surveys",
      "domestic-epcs",
      "defect-reporting",
    ],
    nearby: ["boston", "bourne", "grantham"],
  },
  {
    slug: "grantham",
    name: "Grantham",
    county: "Lincolnshire",
    metaDescription:
      "Independent property surveyor in Grantham — new build snagging, condition surveys, EPCs and landlord reports across Grantham, Barrowby, Gonerby and surrounding villages.",
    intro:
      "Grantham's position on the East Coast Main Line has made it one of Lincolnshire's busiest property markets — commuters, first-time buyers and investors all competing for a stock that runs from Victorian terraces to brand-new estates.",
    paragraphs: [
      "The town has seen significant new build activity at sites around Barrowby Gate, Poplar Farm and the southern fringes. For buyers taking handover of a new home, an independent snagging survey — carried out before or shortly after completion — captures the finish defects, misaligned joinery and incomplete works that developers are obliged to resolve under warranty.",
      "Grantham's older stock brings its own questions: solid-wall Victorian terraces near the town centre, stone properties in surrounding villages like Great Gonerby and Barrowby, and inter-war semis along the arterial roads. Condition surveys, roof surveys and EPCs are the most requested services here, alongside landlord inspections for the town's healthy rental market.",
    ],
    popularServices: [
      "new-build-snagging-surveys",
      "property-condition-surveys",
      "domestic-epcs",
      "roof-surveys",
    ],
    nearby: ["sleaford", "bourne", "lincoln"],
  },
  {
    slug: "sleaford",
    name: "Sleaford",
    county: "Lincolnshire",
    metaDescription:
      "Independent property surveyor in Sleaford — EPCs, condition surveys, snagging and landlord inspection reports across Sleaford, Ruskington, Heckington and mid-Lincolnshire villages.",
    intro:
      "Sleaford blends a historic market-town core of stone and brick period property with steady modern growth to the north and west — a mix that keeps every one of our services in regular demand.",
    paragraphs: [
      "Around the town centre and along the older approaches, buyers encounter solid-wall Victorian houses and older stone cottages where damp management, roof condition and past alterations are the key survey questions. An independent condition survey gives a plain-English picture of what needs attention now, soon, or not at all.",
      "Newer estates in Sleaford, Ruskington, Heckington and the surrounding villages generate consistent demand for snagging surveys and EPCs, while RAF-linked rental demand across mid-Lincolnshire makes landlord inspection reports a staple for local letting agents and private landlords alike.",
    ],
    popularServices: [
      "property-condition-surveys",
      "domestic-epcs",
      "new-build-snagging-surveys",
      "landlord-inspection-reports",
    ],
    nearby: ["lincoln", "grantham", "boston"],
  },
  {
    slug: "louth",
    name: "Louth",
    county: "Lincolnshire",
    metaDescription:
      "Independent property surveyor in Louth — condition surveys, defect and damp reports, EPCs and roof surveys across Louth, the Lincolnshire Wolds and surrounding villages.",
    intro:
      "Known as the capital of the Lincolnshire Wolds, Louth is rich in Georgian and Victorian townhouses, listed buildings and character cottages — beautiful property that deserves a survey written by someone who understands old buildings.",
    paragraphs: [
      "Period homes in and around Louth's conservation area often feature solid walls, lime mortar, original sash windows and complex roofs. The most valuable thing a survey can do here is distinguish normal old-building behaviour from genuine defects — avoiding both unnecessary panic and expensive surprises. Condition surveys and targeted damp or defect reports are the practice's most requested services in the town.",
      "Out in the Wolds villages — and towards the coast at Alford and Spilsby — farmhouses, barn conversions and estate cottages raise similar questions, alongside EPCs for sales and lettings. Roof surveys are particularly popular on older Louth stock, where slate coverings and leadwork are frequently at or beyond their serviceable life.",
    ],
    popularServices: [
      "property-condition-surveys",
      "defect-reporting",
      "roof-surveys",
      "domestic-epcs",
    ],
    nearby: ["skegness", "lincoln", "boston"],
  },
  {
    slug: "bourne",
    name: "Bourne",
    county: "Lincolnshire",
    metaDescription:
      "Independent property surveyor in Bourne — new build snagging at Elsea Park and beyond, EPCs, condition surveys and landlord reports across Bourne, Market Deeping and south Lincolnshire.",
    intro:
      "Bourne is one of south Lincolnshire's fastest-growing towns, with large-scale development at Elsea Park and steady infill across the town — making it a hotspot for new build snagging surveys.",
    paragraphs: [
      "Buyers completing on new homes in Bourne increasingly commission an independent snagging survey before handover. A forensic room-by-room inspection captures paint and plaster defects, misaligned doors and windows, incomplete sealant and service issues in a developer-ready schedule — so problems are fixed under warranty rather than at your expense.",
      "Bourne's established stock — stone cottages near the town centre, inter-war semis and 1970s–90s estates — keeps condition surveys and EPCs in steady demand, while nearby Market Deeping, Crowland and the Deepings corridor add a healthy rental market where landlord inspection reports protect deposits and compliance.",
    ],
    popularServices: [
      "new-build-snagging-surveys",
      "domestic-epcs",
      "property-condition-surveys",
      "landlord-inspection-reports",
    ],
    nearby: ["spalding", "grantham", "boston"],
  },
  {
    slug: "horncastle",
    name: "Horncastle",
    county: "Lincolnshire",
    metaDescription:
      "Property condition surveys & EPCs in Horncastle — independent, accredited surveyor for period homes, Wolds-edge cottages and rental property in Horncastle and surrounding villages.",
    intro:
      "A handsome Georgian market town on the edge of the Lincolnshire Wolds, Horncastle is rich in period property — listed townhouses, brick-and-pantile cottages and conversion projects — the kind of buildings where an independent condition survey earns its fee many times over.",
    paragraphs: [
      "Horncastle's housing stock skews old: solid walls, lime mortar, original roofs and a conservation-area core where buildings have been altered many times over two centuries. For buyers, a property condition survey here is less a formality and more a necessity — distinguishing normal old-building character from genuine defects in damp, roof coverings and structural movement. Plain-English, RICS-aligned reporting gives you the confidence to negotiate, proceed or walk away.",
      "EPCs are the other staple in the town: required for every sale and letting, and particularly consequential for Horncastle's older, solid-wall homes, which often sit in the lower bands where landlord compliance rules bite. As an accredited assessor I combine the certificate itself with practical guidance on which improvements genuinely move a period property's rating — and which waste money. Coverage extends to the surrounding villages, from Tetford and the Wolds hamlets down to Coningsby and Woodhall Spa.",
    ],
    popularServices: [
      "property-condition-surveys",
      "domestic-epcs",
      "defect-reporting",
      "roof-surveys",
    ],
    nearby: ["woodhall-spa", "coningsby", "louth"],
  },
  {
    slug: "woodhall-spa",
    name: "Woodhall Spa",
    county: "Lincolnshire",
    metaDescription:
      "Property condition surveys & EPCs in Woodhall Spa — independent surveyor for the village's Edwardian villas, character homes and high-value property, plus Kirkstead and surrounding areas.",
    intro:
      "Woodhall Spa is unlike anywhere else in Lincolnshire — an Edwardian spa village of broad tree-lined avenues, distinctive villas and high-value character homes set among the pines. Property here deserves survey advice written with that character in mind.",
    paragraphs: [
      "The village's signature stock — substantial Edwardian and inter-war villas, many extended and modernised in stages — rewards a careful condition survey. Original roofs nearing the end of their life, timber bay construction, early cavity walls and decades of incremental alteration are the recurring themes; a traffic-light, RICS-aligned report tells you which matter and which don't, before you commit at Woodhall Spa's price point.",
      "EPCs are in constant demand across the village for sales, lettings and holiday accommodation — and older character homes here often rate lower than owners expect, making independent energy advice valuable alongside the certificate. Coverage includes Kirkstead, Martin and the villages towards Horncastle and Coningsby, with same-week availability for most EPC appointments.",
    ],
    popularServices: [
      "property-condition-surveys",
      "domestic-epcs",
      "energy-efficiency-advice",
      "roof-surveys",
    ],
    nearby: ["horncastle", "coningsby", "lincoln"],
  },
  {
    slug: "coningsby",
    name: "Coningsby",
    county: "Lincolnshire",
    metaDescription:
      "EPCs & property condition surveys in Coningsby and Tattershall — independent, accredited surveyor serving the village's strong rental market, service families and home buyers.",
    intro:
      "Home to RAF Coningsby, the village and neighbouring Tattershall support one of the strongest rental markets in mid-Lincolnshire — which makes EPCs, landlord reports and pre-purchase condition surveys the daily bread of property business here.",
    paragraphs: [
      "The RAF station shapes Coningsby's market: a steady flow of service families and contractors renting locally means landlords need valid EPCs before every new tenancy, and the minimum-standard rules make the rating itself a commercial question, not just a compliance one. I provide accredited EPCs across Coningsby and Tattershall with same-week turnaround, plus photographed landlord inspection reports that protect deposits and keep lettings compliant.",
      "For buyers, the stock runs from post-war ex-service housing and bungalows through to modern developments and older village cottages near the church and the River Bain. A property condition survey gives a clear, independent picture before you commit — particularly worthwhile on older cottages and on ex-rental properties where maintenance may have lagged. Coverage extends to Tattershall, New York, Mareham le Fen and the surrounding Fen-edge villages.",
    ],
    popularServices: [
      "domestic-epcs",
      "property-condition-surveys",
      "landlord-inspection-reports",
      "defect-reporting",
    ],
    nearby: ["horncastle", "woodhall-spa", "boston"],
  },
];

export const getArea = (slug) => AREAS.find((a) => a.slug === slug);
